/**
 * Client-side mirror of the backend equipment assignment rule (#10363).
 *
 * `UpsertEquipmentRequestValidator` requires each equipment item to satisfy
 * `(AssetId > 0 && MineLocationId == -1)` XOR `(AssetId == -1 && MineLocationId > 0)`,
 * otherwise the inspection POST is rejected with a 400
 * ("Must assign either assetId or mineLocationId").
 *
 * The invalid state arises in practice when a new bucket is installed on a
 * machine whose site has no laydown-yard mine location: the outgoing bucket is
 * detached from the asset (assetId -1) but there is no yard to move it to
 * (mineLocationId -1).
 */

/** Single place for the support contact so the wording can be updated in one edit. */
export const DIGITAL_SUPPORT_CONTACT = 'Digital Support';

export interface EquipmentAssignment {
  assetId: number;
  mineLocationId: number;
}

/**
 * True when exactly one of assetId / mineLocationId is a valid (> 0) id and the
 * other is exactly the -1 sentinel — the backend rejects any other value (e.g. 0)
 * in the unused slot.
 */
export function isValidEquipmentAssignment({
  assetId,
  mineLocationId,
}: EquipmentAssignment): boolean {
  return (
    (assetId > 0 && mineLocationId === -1) ||
    (assetId === -1 && mineLocationId > 0)
  );
}

/** True when the item has been detached from its asset but not moved to a yard. */
export function isUnassignedEquipment({
  assetId,
  mineLocationId,
}: EquipmentAssignment): boolean {
  return assetId <= 0 && mineLocationId <= 0;
}

export function findInvalidEquipmentAssignments<T extends EquipmentAssignment>(
  items: T[],
): T[] {
  return items.filter((item) => !isValidEquipmentAssignment(item));
}

/**
 * Message shown when the inspector tries to install a new bucket on a machine
 * that already has a bucket, but the site has no laydown yard to move it to.
 */
export function buildNoLaydownYardCreationMessage(
  currentBucketName?: string,
): string {
  const bucket = currentBucketName
    ? `the current bucket "${currentBucketName}"`
    : 'the current bucket';
  return (
    `A new bucket cannot be installed on this machine because the site has no laydown yard to move ${bucket} to. ` +
    `Sync and try again, or contact ${DIGITAL_SUPPORT_CONTACT} to have a laydown yard added for this site.`
  );
}

/**
 * Message shown when the pre-submit sanity check finds equipment the server
 * would reject. Names each offending item so the inspector can tell support
 * exactly which bucket is affected.
 */
export function buildInvalidEquipmentSubmitMessage(
  items: Array<EquipmentAssignment & { name: string }>,
): string {
  const lines = items.map((item) => {
    if (isUnassignedEquipment(item)) {
      return `Bucket "${item.name}" has been removed from the machine, but there is no laydown yard at this site to move it to.`;
    }
    if (item.assetId > 0 && item.mineLocationId > 0) {
      return `Bucket "${item.name}" is assigned to both a machine and a mine location.`;
    }
    return `Bucket "${item.name}" has an invalid location assignment.`;
  });

  return [
    'This inspection cannot be submitted.',
    ...lines,
    `Please contact ${DIGITAL_SUPPORT_CONTACT} to investigate. Your inspection has been kept on this device.`,
  ].join('\n');
}
